"use client";

import React, { useState } from "react";
import Section from "@/components/partials/section";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";

type ContentItem = {
    title: string;
    description: string;
    category: string;
};

type FAQsBlockProps = {
    data: {
        alignment: string;
        title: string;
        description: string;
        content: ContentItem[];
    };
    containerClassName?: string;
    showTabs?: boolean;
    defaultCategory?: string;
};

const FAQsBlock = ({ data, showTabs = false, defaultCategory = "General", containerClassName = '' }: FAQsBlockProps) => {
    const { alignment, title, description, content } = data;

    // Build category count map
    const categoryMap: Record<string, number> = {};
    content.forEach((item) => {
        const category = item.category || "General";
        categoryMap[category] = (categoryMap[category] || 0) + 1;
    });

    const tabs = Object.entries(categoryMap).map(([label, count]) => ({
        label,
        count,
    }));

    const [activeTab, setActiveTab] = useState(tabs[0]?.label ?? "General");

    // Determine which content to show
    const selectedCategory = showTabs ? activeTab : defaultCategory;
    const filteredContent = content.filter((item) => item.category === selectedCategory);

    return (
        <Section
            id="faqs"
            containerClassName={containerClassName}
            header={{
                classGroup: {
                    h1: "text-3xl sm:text-6xl",
                },
                alignment: alignment,
                title: title,
                description: description,
            }}
        >
            {/* Tabs */}
            {showTabs && (
                <div className="flex flex-wrap justify-center gap-3 mb-10">
                    {tabs.map(({ label, count }) => {
                        const isActive = activeTab === label;
                        const baseStyles =
                            "inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors";
                        const activeStyles = "bg-neutral-800 text-white font-extrabold";
                        const inactiveStyles = "bg-neutral-950 text text-gray-300 hover:bg-neutral-900";

                        return (
                            <button
                                key={label}
                                onClick={() => setActiveTab(label)}
                                className={`${baseStyles} ${isActive ? activeStyles : inactiveStyles}`}
                            >
                                {label}
                                <span className="ml-2 inline-flex items-center justify-center w-6 h-6 text-xs font-semibold text-white bg-[var(--background2-color)] rounded-full">
                                    {count}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* FAQ Content */}
            <div className="max-w-2xl mx-auto divide-y divide-[var(--border-color)]">
                {filteredContent.map((item, index) => (
                    <div key={index} className="py-8 first:pt-0 last:pb-0">
                        <div className="flex gap-x-5">
                            <QuestionMarkCircleIcon className="flex-shrink-0 w-6 h-6 text-purple-400 mt-1" />
                            <div className="flex flex-col">
                                <h3 className="text-[var(--headline-1-color)] font-semibold md:text-lg">
                                    {item.title}
                                </h3>
                                <p className="mt-1">{item.description}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {/* JSON-LD for FAQPage (Rich Results) */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'FAQPage',
                        mainEntity: filteredContent.map((item) => ({
                            '@type': 'Question',
                            name: item.title,
                            acceptedAnswer: {
                                '@type': 'Answer',
                                text: item.description,
                            },
                        })),
                    }),
                }}
            />
        </Section>
    );
};

export default FAQsBlock;
